import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import ItemCard from "../../components/ItemCard";
import * as S from "./styles";

import { api } from "../../services/api";
import {
  fireDeleteMsg,
  fireSuccessMsg,
  onRequestError,
} from "../../services/functions";
import { useLoading } from "../../contexts/LoadingContext";
import { useMediaLib } from "../../contexts/MediaLibContext";

const ListHighlights = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [highlightTitle, setHighlightTitle] = useState("");
  const [highlightImage, setHighlightImage] = useState("");

  const { setLoading } = useLoading();
  const { setShowMediaLib, setSelectionCallback } = useMediaLib();
  const location = useLocation();
  const locationRef = useRef();

  const routeName = "products";

  useEffect(() => {
    if (location && location !== locationRef.current) {
      locationRef.current = location;
      getHighlights();
    }
    // eslint-disable-next-line
  }, [location]);

  const getHighlights = async () => {
    setLoading(true);
    await api
      .get(`/api/highlights`)
      .then(response => {
        setProducts(response.data);
      })
      .catch(e => {
        onRequestError("Não foi possível listar os destaques!", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const toggleHighlight = async product => {
    setLoading(true);
    await api
      .patch(`/api/products/${product.id}/highlight`, {
        highlight: 0, // In this page, we only remove highlights
      })
      .then(response => {
        fireSuccessMsg(response.data.msg, getHighlights);
      })
      .catch(e => {
        onRequestError("Não foi possível remover o destaque!", e);
        setLoading(false);
      });
  };

  const handleEditHighlight = product => {
    setEditingProduct(product);
    setHighlightTitle(product.highlight_title || product.name);
    setHighlightImage(product.highlight_image || "");
  };

  const closeEditModal = () => {
    setEditingProduct(null);
    setHighlightTitle("");
    setHighlightImage("");
  };

  const openMediaLib = () => {
    setSelectionCallback(() => path => {
      setHighlightImage(path);
    });
    setShowMediaLib(true);
  };

  const handleSaveHighlight = async e => {
    e.preventDefault();
    setLoading(true);

    await api
      .patch(`/api/products/${editingProduct.id}/highlight-data`, {
        highlight_title: highlightTitle,
        highlight_image: highlightImage,
      })
      .then(response => {
        fireSuccessMsg(response.data.msg, () => {
          getHighlights();
          closeEditModal();
        });
      })
      .catch(e => {
        onRequestError("Não foi possível salvar os dados do destaque!", e);
        setLoading(false);
      });
  };

  const onDeleteSuccess = (msg, deletedItems) => {
    fireSuccessMsg(msg);
    const productsCopy = products.filter(p => !deletedItems.find(d => d.id === p.id));
    setProducts(productsCopy);
  };

  const deleteSingleProduct = product => {
    fireDeleteMsg(product.name, async () => {
      setLoading(true);
      await api
        .delete(`/api/products/${product.id}`)
        .then(response => onDeleteSuccess(response.data.msg, [product]))
        .catch(e => onRequestError(e.msg, e))
        .finally(() => setLoading(false));
    });
  };

  return (
    <div className="h-padded v-padded-lg">
      <div className="flexbox v-centered h-spaced-between" style={{ marginBottom: '2rem' }}>
        <h1>Destaques da Home</h1>
        <p style={{ color: 'var(--grey4)', maxWidth: '400px' }}>
          Estes produtos aparecem na seção "Melhores custos-benefícios" da página principal.
        </p>
      </div>

      <div>
        {products.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem', background: 'var(--ice)', borderRadius: 'var(--radius-md)' }}>
            <span className="material-symbols-rounded" style={{ fontSize: '48px', color: 'var(--grey6)', marginBottom: '1rem' }}>star_outline</span>
            <span style={{ display: "block", fontSize: "20px", color: 'var(--lead)' }}>
              Nenhum produto em destaque no momento.
            </span>
            <p style={{ marginTop: '0.5rem' }}>Vá em "Produtos" e marque a opção de destaque em um produto.</p>
          </div>
        )}
        
        {products.map((item, i) => (
          <ItemCard
            index={i}
            item={item}
            routeName={routeName}
            deleteHandler={deleteSingleProduct}
            highlightHandler={toggleHighlight}
            editHighlightHandler={handleEditHighlight}
            warnings={[
              {
                label: "Sem modelos!",
                condition: (item.model_types || []).length === 0,
              },
              {
                label: "Sem mídia!",
                condition: (item.media || []).length === 0,
              },
            ]}
            key={i}
          />
        ))}
      </div>

      {editingProduct && (
        <S.Overlay>
          <S.HighlightForm onSubmit={handleSaveHighlight}>
            <h2>Personalizar Destaque</h2>
            
            <div className="form-group">
              <label>Título no Destaque</label>
              <input 
                type="text" 
                value={highlightTitle} 
                onChange={e => setHighlightTitle(e.target.value)}
                placeholder="Ex: Guarita Simples com Preço Especial"
              />
              <p style={{ fontSize: '12px', color: 'var(--grey4)', marginTop: '0.25rem' }}>
                Este título aparecerá apenas na Home. O nome original do produto não será alterado.
              </p>
            </div>

            <div className="form-group">
              <label>Imagem do Destaque</label>
              <div className="image-preview">
                {highlightImage ? (
                  <img src={`${api.getUri()}/storage/${highlightImage}`} alt="Preview" />
                ) : (
                  <span>Nenhuma imagem selecionada</span>
                )}
              </div>
              <button type="button" className="btn blue sm" onClick={openMediaLib}>
                {highlightImage ? "Trocar Imagem" : "Selecionar Imagem"}
              </button>
              <p style={{ fontSize: '12px', color: 'var(--grey4)', marginTop: '0.25rem' }}>
                Esta imagem aparecerá apenas na Home. A galeria do produto continuará a mesma.
              </p>
            </div>

            <div className="controls">
              <button type="button" className="btn ice" onClick={closeEditModal}>Cancelar</button>
              <button type="submit" className="btn blue">Salvar Alterações</button>
            </div>
          </S.HighlightForm>
        </S.Overlay>
      )}
    </div>
  );
};

export default ListHighlights;
